import images from '@assets/images/index'
import { TeamTopPlayersType } from '@utils/Variables'
import {
  getBaseURL,
  isBetaVersion,
  renderPlayed
} from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { appSessionStorage } from '@utils/storageUtils'
import type { RadioChangeEvent } from 'antd'
import {
  Col,
  Flex,
  Progress,
  Radio,
  Row,
  Spin,
  Switch,
  Table,
  message
} from 'antd'
import { lazy, memo, useEffect, useState } from 'react'

import { Link, useLocation, useNavigate } from '@suntex.core/nav'
import {
  Database_Procs_GetTeamHistory_Item,
  Database_Procs_TeamTopPlayers_Item
} from '@suntex.fim/api-client'
import { getJtfModeOptions } from '@suntex.fim/assessment'
import {
  GymSummaryProgress,
  KsSummaryProgress,
  SkillSetSummaryProgress
} from '@suntex.fim/assessment/lazy'
import { useAuth } from '@suntex.fim/auth/react'
import {
  $EntityLevel,
  $GymMode,
  $SpecialGradeLevel,
  ChartSource,
  EntityLevel,
  GoalsIndexPhase,
  GymMode,
  JtfMode,
  KnowAndShowLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { getFlags } from '@suntex.fim/flags'
import { useRotateDevice } from '@suntex.fim/framework'
import { StickerCount } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { LevelSelect, Modal, TextArea, Tooltip } from '@suntex.fim/ui'

import { Button } from '../../components/ui'
import {
  useDashboardConfigQuery,
  useFluencyGamesQuery,
  useRecentActivityQuery,
  useSaveDashboardConfig,
  useSubmitAppFeedback,
  useTeamHistoryQuery,
  useTeamIndexQuery
} from '../../hooks/useDashboardQueries'

import { ActivityMode } from './LearningCenter/util'

const PROGRESS_BAR_WIDTH = 91
const PROGRESS_BAR_HEIGHT = 23
const DECIMAL_PRECISION = 10
const ICON_SIZE = 18
const MIN_TOP_PLAYERS = 3
const GUTTER_SIZE = 10

const goalsIndexPhaseOptions: { key: GoalsIndexPhase; label: string }[] = [
  { key: GoalsIndexPhase.Early, label: 'Early' },
  { key: GoalsIndexPhase.Elementary, label: 'Elementary' },
  { key: GoalsIndexPhase.Intermediate, label: 'Intermediate' },
  { key: GoalsIndexPhase.Advanced, label: 'Advanced' }
]

const options = objToLevSelOpts(TeamTopPlayersType)
const GoalIndexChart = lazy(
  () => import('@components/Charts/GoalIndex/GoalIndexChartWrapper')
)
const LineChart = lazy(() => import('@components/Charts/LineChart'))
const JustTheFactsGraph = lazy(
  () => import('../../components/Charts/JustTheFactsGraph')
)

// Memoized chart components to prevent unnecessary re-renders
const MemoizedGoalIndexChart = memo(GoalIndexChart)
const MemoizedLineChart = memo(LineChart)
// Reusable component for rendering progress bars
function ProgressBar({
  label,
  percent,
  showData
}: {
  label: string
  percent: number
  showData?: boolean
}) {
  return (
    <div className="player-chart-gym skill-set-header dashboard-important-fact fw-normal">
      <span>{label}</span>
      <Progress
        percent={percent || 0}
        size={[PROGRESS_BAR_WIDTH, PROGRESS_BAR_HEIGHT]}
        strokeColor="#3FB2FF"
        className="progress-bar-percentright"
        showInfo={showData}
      />
    </div>
  )
}
let reasonTitle: string[] = [
  'I’m facing problems using new version.',
  'I’m not able to find my links using this version.',
  'I more liked the classic version',
  'Other'
]
if (isBetaVersion()) {
  reasonTitle = [
    'The Beta version has issues that make it harder to use in class.',
    'Features I use are not accessible in the Beta version.',
    'I prefer FIM Classic because I am used to it.',
    'Other'
  ]
}

/**
 * returns JSX for dashboard
 */
function Dashboard() {
  useRotateDevice()
  const location = useLocation()
  const pathSegment = location.pathname.split('/')[1] || '' // Ensure it's always a string
  const entityLevel = $EntityLevel.fromSlug(pathSegment)
  const {
    goalsIndexPhase,
    goalsIndexPhaseLoginId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    dashboardConfigData,
    schoolYear,
    assessmentGrade: schoolGrade
  } = appSessionStorage

  const { login: user, refresh: refetchUser } = useAuth()
  const team = useOptionalTeamInfo()
  const isSchoolContext = entityLevel === EntityLevel.School
  const isPlayer = user?.entityLevel === EntityLevel.Player
  const isTeamDataEnabled =
    !isPlayer && !isSchoolContext && !!team?.id && !!user?.id

  // --- UI State ---
  const [editDashboardModal, setEditDashboardModal] = useState(false)
  const [switchToClassicdModal, setSwitchClassicModal] = useState(false)
  const [day, setDay] = useState(0)
  const [phase, setPhase] = useState<GoalsIndexPhase>(GoalsIndexPhase.Early)
  const [mode, setMode] = useState(ActivityMode.Day)
  const [value, setValue] = useState('')
  const [reason, setReason] = useState<string>('')
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(event.target.value)
  }
  const [insideModalDashboardConfig, setInsideModalDashboardConfig] = useState<{
    [key: string]: boolean
  }>({})

  // --- Queries ---
  const { data: teamIndexData, isLoading: topPlayersLoading } =
    useTeamIndexQuery(team?.id, user?.id, day, {
      enabled: isTeamDataEnabled
    })
  const topPlayers = isTeamDataEnabled ? (teamIndexData?.topPlayers ?? []) : []
  const playerOfTheDay = isTeamDataEnabled
    ? (teamIndexData?.playersOfDay ?? [])
    : []

  const { data: dashboardConfig } = useDashboardConfigQuery(user?.id, {
    enabled: !dashboardConfigData
  })
  const checkedDashboardConfig: Record<string, boolean> =
    dashboardConfig ??
    (dashboardConfigData as Record<string, boolean> | undefined) ??
    {}

  const { data: recentActivityData = [] } = useRecentActivityQuery(team?.id, {
    enabled: isTeamDataEnabled
  })

  const { data: teamFluencyGame } = useFluencyGamesQuery(
    team?.id,
    team?.year,
    team?.grade,
    { enabled: isTeamDataEnabled }
  )

  const { data: teamHistory, isLoading: teamHistoryLoading } =
    useTeamHistoryQuery(team?.id, mode, {
      enabled: !!team?.id && !isSchoolContext
    })

  // --- Mutations ---
  const saveDashboardConfigMutation = useSaveDashboardConfig()
  const submitAppFeedbackMutation = useSubmitAppFeedback()
  const columns = [
    {
      title: 'Player Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (
        displayName: string,
        record: Database_Procs_TeamTopPlayers_Item
      ) => {
        return displayName ? (
          displayName
        ) : (
          <Link
            to={`/team/${team?.id}/team-management/team-roster?noneUserId=${record.userId}`}
          >
            None
          </Link>
        )
      }
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId'
    },
    {
      title: 'Sticker Count',
      dataIndex: 'stickers',
      key: 'stickers',
      render: (stickers: number) => <StickerCount count={stickers} day={day} />
    }
  ]
  const navigate = useNavigate()

  const onChange = (e: RadioChangeEvent) => {
    setValue(e.target.value)
  }

  // Sync phase from session storage
  useEffect(() => {
    if (user?.id && goalsIndexPhaseLoginId === user.id) {
      setPhase(goalsIndexPhase)
    }
  }, [user?.id])

  // Persist query result to session storage on first load
  useEffect(() => {
    if (!dashboardConfigData && dashboardConfig) {
      appSessionStorage.dashboardConfigData = dashboardConfig
    }
  }, [dashboardConfig])
  const jtfMode =
    getJtfModeOptions(teamFluencyGame?.jtfModes)[0]?.key ?? JtfMode.WholeNumbers

  const modeLevelOptions = [
    {
      key: 1,
      label: 'Weekly'
    },
    {
      key: 2 as const,
      label: 'Monthly'
    },
    {
      key: 3 as const,
      label: 'Last 5 Months'
    }
  ]
  function containsNonZeroValue(obj: Database_Procs_GetTeamHistory_Item) {
    return Object.values(obj).some((value) => value !== 0)
  }
  // Handle change when a checkbox is toggled
  const handleCheckboxChange = (name: string) => {
    setInsideModalDashboardConfig((prevState) => ({
      ...prevState,
      [name]: !prevState[name]
    }))
  }
  const closeModal = () => {
    setInsideModalDashboardConfig(checkedDashboardConfig) // Reset to original state
    setEditDashboardModal(false) // Close modal
  }
  const saveDashboardConfig = async () => {
    setEditDashboardModal(false)
    await saveDashboardConfigMutation.mutateAsync({
      loginId: user!.id,
      config: insideModalDashboardConfig
    })
    appSessionStorage.dashboardConfigData = insideModalDashboardConfig
  }
  const handleSwitchClassicModal = async () => {
    await submitAppFeedbackMutation.mutateAsync({
      loginId: user!.id,
      reasonTitle: value,
      reasonDescription: reason
    })
    await refetchUser()
    message.success('Feedback submitted successfully')
    setValue('')
    setReason('')
    setSwitchClassicModal(false)
  }

  const handleCancelSwitchClassicModal = () => {
    setValue('')
    setReason('')
    setSwitchClassicModal(false)
  }
  const handleSwitchToClassicdModalOpen = () => {
    if (user?.appFeedbackExists) {
      window.location.href = `${getBaseURL()}/team`
    } else {
      setSwitchClassicModal(true)
    }
  }
  return (
    <Row className="teacher-dashboard">
      {/* Goal Index section */}
      <Col flex="1" className="teacher-dashboard__main">
        <Flex vertical className="main-content" gap="30px">
          {/* top */}
          <Flex
            className="quick-links quick-links-main"
            justify="space-between"
            align="center"
            gap={10}
          >
            <Flex
              align="center"
              className="quick-links__title quick-link-btn"
              gap="10px"
            >
              <span className="fw-bold">Quick Links</span>

              {(entityLevel === EntityLevel.Team
                ? [
                    checkedDashboardConfig.wallOfFameTool && {
                      label: 'Wall of Fame Tool',
                      to: `/team/${team?.id}/rankings/wall-of-fame-tool`
                    },
                    checkedDashboardConfig.focusMode && {
                      label: 'Focus Mode',
                      to: `/team/${team?.id}/focus-mode`
                    }
                  ]
                : entityLevel === EntityLevel.School
                  ? [
                      checkedDashboardConfig.wallOfFameTool && {
                        label: 'Top Players & Teams',
                        to: `/school/${user?.schoolId}/rankings/top-players-teams`
                      },
                      {
                        label: 'Wall of Fame Tool',
                        to: `/school/${user?.schoolId}/rankings/wall-of-fame-tool`
                      }
                    ]
                  : []
              )
                .filter((link) => typeof link === 'object')
                .map((link) => link)
                .map((link) => (
                  <Button
                    key={link.label}
                    intent="secondary"
                    onClick={() => navigate(link.to)}
                  >
                    {link.label}
                  </Button>
                ))}
            </Flex>
            <Flex gap={12} className="quick-links__right leading-1">
              {getFlags().switchToClassic && (
                <Button
                  intent="secondary"
                  onClick={handleSwitchToClassicdModalOpen}
                >
                  Switch to Classic FIM
                </Button>
              )}
              <Modal
                className="switchtoclassic-modal"
                closable={false}
                title="Reason for switching back to classic FIM"
                open={switchToClassicdModal}
                onOk={() => setSwitchClassicModal(false)}
                onCancel={() => setSwitchClassicModal(false)}
                width={633}
                footer={[
                  <Flex justify="space-between" align="center" key={value}>
                    <div>
                      <Button
                        shape="round"
                        intent="secondary"
                        key="back"
                        onClick={handleCancelSwitchClassicModal}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div>
                      <Button
                        shape="round"
                        intent="primary"
                        key="submit"
                        onClick={handleSwitchClassicModal}
                      >
                        Submit
                      </Button>
                    </div>
                  </Flex>
                ]}
              >
                <Flex vertical>
                  <div>
                    <p className="switchtoclassic-modal__title">
                      Please provide us a reason why are you switching back to
                      classic FIM so that we can improve our new version to
                      serve you better.
                    </p>
                  </div>
                  <Radio.Group onChange={onChange} value={value}>
                    <Row gutter={[ICON_SIZE, ICON_SIZE]}>
                      {reasonTitle.map((r) => (
                        <Col span={24} key={r}>
                          <div>
                            <Radio value={r}>
                              <p className="switchtoclassic-modal__option">
                                {r}
                              </p>
                            </Radio>
                            {value === r && (
                              <TextArea
                                value={reason}
                                placeholder="Write your reason here"
                                onChange={handleChange}
                                className="switchtoclassic-modal__textarea"
                              />
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Radio.Group>
                </Flex>
              </Modal>
              <Button
                aria-label="Edit Dashboard"
                intent="icon"
                onClick={() => {
                  setInsideModalDashboardConfig(checkedDashboardConfig)
                  setEditDashboardModal(true)
                }}
              >
                <images.ElementPlus />
              </Button>
              {/* Edit Dashboard Modal Starts */}
              <Modal
                closable={false}
                title="Edit Dashboard"
                open={editDashboardModal}
                onOk={saveDashboardConfig}
                onCancel={closeModal}
                width={900}
                className="edit-dashboard"
                footer={[
                  <>
                    <Button
                      shape="round"
                      intent="secondary"
                      key="back"
                      onClick={closeModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      shape="round"
                      intent="tertiary"
                      key="submit"
                      onClick={saveDashboardConfig}
                    >
                      Save Changes
                    </Button>
                  </>
                ]}
              >
                <Flex vertical className="section-to">
                  <div className="title">
                    <Flex align="center" justify="space-between">
                      <p className="fw-bold">Quick Links</p>
                      <Flex gap={10}>
                        <p className="fw-bold">Show Data on Grid</p>
                        <Switch
                          checked={insideModalDashboardConfig.showDataOnGrid}
                          onChange={() =>
                            handleCheckboxChange('showDataOnGrid')
                          }
                        />
                      </Flex>
                    </Flex>
                    <p className="text-chalkboard-txt leading-1">
                      Select which quick links you’d like to see on your
                      dashboard.
                    </p>
                  </div>
                  <Row
                    gutter={[GUTTER_SIZE, GUTTER_SIZE]}
                    className="shown-game-list"
                  >
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.focusMode}
                            onChange={() => handleCheckboxChange('focusMode')}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Focus Mode</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.wallOfFameTool}
                            onChange={() =>
                              handleCheckboxChange('wallOfFameTool')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Wall of Fame Tool</span>
                      </Flex>
                    </Col>
                  </Row>
                </Flex>
                <Flex vertical className="section-to">
                  <div className="title">
                    <p className="fw-bold">Sections</p>
                    <p className="text-chalkboard-txt leading-1">
                      Select which reports you’d like to see on your dashboard.
                      {/* <span className="text-mathRed">
                        &nbsp;Max of 5 reports.
                      </span> */}
                    </p>
                  </div>
                  <Row
                    gutter={[GUTTER_SIZE, GUTTER_SIZE]}
                    className="shown-game-list"
                  >
                    {getFlags().player.goals && (
                      <Col xs={24} sm={24} md={8} lg={8}>
                        <Flex className="full-checkbox">
                          <div className="pretty p-icon p-toggle">
                            <input
                              type="checkbox"
                              checked={insideModalDashboardConfig.goalsIndex}
                              onChange={() =>
                                handleCheckboxChange('goalsIndex')
                              }
                            />
                            <div className="state p-on">
                              <images.CheckboxCheckedLg />
                            </div>
                            <div className="state p-off text-primary">
                              <images.CheckboxEmptyLg />
                            </div>
                          </div>
                          <span className="fw-bold">Goals Index</span>
                        </Flex>
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={
                              insideModalDashboardConfig.teamActivityChart
                            }
                            onChange={() =>
                              handleCheckboxChange('teamActivityChart')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Team Activity Chart</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.playersOfTheDay}
                            onChange={() =>
                              handleCheckboxChange('playersOfTheDay')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Player of the day</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.topPlayers}
                            onChange={() => handleCheckboxChange('topPlayers')}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Top Players</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.teamActivity}
                            onChange={() =>
                              handleCheckboxChange('teamActivity')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Recent Activity</span>
                      </Flex>
                    </Col>
                  </Row>
                </Flex>
                <Flex vertical className="section-to">
                  <div className="title">
                    <p className="fw-bold">Performance List</p>
                    <p className="text-chalkboard-txt leading-1">
                      Select which modules you’d like to see on your dashboard.
                    </p>
                  </div>
                  <Row
                    gutter={[GUTTER_SIZE, GUTTER_SIZE]}
                    className="shown-game-list"
                  >
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.gyms}
                            onChange={() => handleCheckboxChange('gyms')}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Gyms</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.skillSets}
                            onChange={() => handleCheckboxChange('skillSets')}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Skill Sets</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.knowAndShow}
                            onChange={() => handleCheckboxChange('knowAndShow')}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Know and Show</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={
                              insideModalDashboardConfig.veryImportantFacts
                            }
                            onChange={() =>
                              handleCheckboxChange('veryImportantFacts')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Very Important Facts</span>
                      </Flex>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Flex className="full-checkbox">
                        <div className="pretty p-icon p-toggle">
                          <input
                            type="checkbox"
                            checked={insideModalDashboardConfig.justTheFacts}
                            onChange={() =>
                              handleCheckboxChange('justTheFacts')
                            }
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedLg />
                          </div>
                          <div className="state p-off text-primary">
                            <images.CheckboxEmptyLg />
                          </div>
                        </div>
                        <span className="fw-bold">Just the Facts</span>
                      </Flex>
                    </Col>
                  </Row>
                </Flex>
              </Modal>
              {/* Edit Dashboard Modal Ends */}
            </Flex>
          </Flex>
          {/* frame 2 */}
          <Flex vertical className="tiles-container">
            <Flex className="dashboard-graph-container" gap={20}>
              {getFlags().player.goals && checkedDashboardConfig.goalsIndex && (
                <div style={{ flex: '0 0 40%' }}>
                  <Flex
                    vertical
                    gap="20px"
                    className="graph-tiles goal-index"
                    style={{ height: '100%' }}
                  >
                    <Flex
                      align="center"
                      className="graph-tiles__title"
                      justify="space-between"
                    >
                      <h4 className="title">
                        {$EntityLevel.describe(entityLevel).label}s Goals Index
                      </h4>
                      <div className="right">
                        <LevelSelect<GoalsIndexPhase>
                          value={phase}
                          onChange={(newLevel) => {
                            appSessionStorage.goalsIndexPhaseLoginId = user?.id
                              ? user.id
                              : 0
                            appSessionStorage.goalsIndexPhase = newLevel
                            setPhase(newLevel)
                          }}
                          options={goalsIndexPhaseOptions}
                        />
                      </div>
                    </Flex>
                    <Flex
                      vertical
                      align="center"
                      className="graph-tiles__graph goal-index-graph"
                    >
                      <MemoizedGoalIndexChart
                        id={
                          isSchoolContext ? user?.schoolId || 0 : team?.id || 0
                        }
                        level={
                          isSchoolContext
                            ? EntityLevel.School
                            : EntityLevel.Team
                        }
                        year={
                          isSchoolContext
                            ? schoolYear || team?.year || ''
                            : team?.year || ''
                        }
                        phase={phase}
                        grade={
                          $SpecialGradeLevel.fromOptional(
                            isSchoolContext
                              ? (schoolGrade ?? team?.grade)
                              : team?.grade
                          ) ?? SpecialGradeLevel.Null
                        }
                        from={ChartSource.Dashboard}
                        entityLevel={entityLevel}
                      />
                    </Flex>
                  </Flex>
                </div>
              )}
              <div
                style={{
                  flex:
                    getFlags().player.goals && checkedDashboardConfig.goalsIndex
                      ? '0 0 60%'
                      : '1 1 100%'
                }}
              >
                {checkedDashboardConfig.teamActivityChart && (
                  <Flex vertical gap="20px" className="graph-tiles goal-index">
                    <Flex
                      align="center"
                      className="graph-tiles__title"
                      justify="space-between"
                    >
                      <h4 className="title">Team Activity</h4>
                      <div className="right">
                        <Flex align="center">
                          <images.StarOnly />
                          <span className="team-rating">
                            {teamFluencyGame?.stickerCount.toLocaleString()}
                          </span>
                        </Flex>
                        {teamHistory && containsNonZeroValue(teamHistory) && (
                          <LevelSelect
                            value={mode}
                            onChange={(newMode) => setMode(newMode)}
                            options={modeLevelOptions}
                          />
                        )}
                      </div>
                    </Flex>
                    <Flex
                      vertical
                      align="center"
                      className="graph-tiles__graph"
                    >
                      {teamHistory && containsNonZeroValue(teamHistory) ? (
                        <MemoizedLineChart
                          mode={mode}
                          chartData={teamHistory}
                          isLoading={teamHistoryLoading}
                        />
                      ) : (
                        'No data available'
                      )}
                    </Flex>
                  </Flex>
                )}
              </div>
            </Flex>
            <div className="team-performance">
              <Flex
                className="team-performance__top"
                justify="space-between"
                align="center"
                wrap
              >
                <h4 className="title">Team Performance</h4>
                <p className="text-sm">
                  Total Students :{' '}
                  <span className="fw-bold">
                    {teamFluencyGame?.players?.length}
                  </span>
                </p>
              </Flex>
              {teamFluencyGame ? (
                <div className="graph-tiles games">
                  {/* Gyms Graph */}
                  {checkedDashboardConfig.gyms && (
                    <div className="available-game">
                      <Flex gap={8} align="center">
                        <div className="game-cover">
                          <img
                            src={images.Gyms}
                            alt="Gyms"
                            className="goal-index-report-icon"
                          />
                        </div>
                        <div className="game-section-name fw-bold">Gyms</div>
                      </Flex>
                      <Flex align="flex-end">
                        <div className="stat graph-container game-data dashboard-bar-main">
                          <GymSummaryProgress
                            level={
                              $GymMode.fromOptionalSlug(
                                String(teamFluencyGame.gymModes?.[0] ?? '')
                              ) ?? GymMode.WholeNumbers
                            }
                            teamData={teamFluencyGame.gymSummary ?? null}
                            showData={checkedDashboardConfig.showDataOnGrid}
                          />
                        </div>
                      </Flex>
                    </div>
                  )}
                  {/* Skill Sets Graph */}
                  {checkedDashboardConfig.skillSets && (
                    <div className="available-game">
                      <Flex gap={8} align="center">
                        <div className="game-cover">
                          <images.SkillsetCover />
                        </div>
                        <div className="game-section-name fw-bold">
                          Skill Sets
                        </div>
                      </Flex>
                      <Flex className="game-data" vertical>
                        <div className="stat graph-container dashboard-bar-main">
                          <SkillSetSummaryProgress
                            teamData={teamFluencyGame.skillSetSummary ?? null}
                            showData={checkedDashboardConfig.showDataOnGrid}
                          />
                        </div>
                      </Flex>
                    </div>
                  )}
                  {/* Know and Show Graph */}
                  {checkedDashboardConfig.knowAndShow && (
                    <div className="available-game">
                      <Flex gap={8} align="center">
                        <div className="game-cover">
                          <img
                            src={images.Wpf}
                            alt="Wpf"
                            className="goal-index-report-icon"
                          />
                        </div>
                        <div className="game-section-name fw-bold">
                          Know and Show
                        </div>
                      </Flex>
                      <Flex className="game-data" vertical>
                        <div className="stat graph-container dashboard-bar-main">
                          <KsSummaryProgress
                            level={teamFluencyGame.knowAndShowLevels?.[0] || 0}
                            teamData={
                              teamFluencyGame.knowAndShowSummary ?? null
                            }
                            showData={checkedDashboardConfig.showDataOnGrid}
                          />
                        </div>
                      </Flex>
                    </div>
                  )}
                  {/* Very Important Facts Graph*/}
                  {checkedDashboardConfig.veryImportantFacts && (
                    <div className="available-game">
                      <Flex gap={8} align="center">
                        <div className="game-cover">
                          <img
                            src={images.Vif}
                            alt="Vif"
                            className="goal-index-report-icon"
                          />
                        </div>
                        <div className="game-section-name fw-bold">
                          Very Important Facts
                        </div>
                      </Flex>
                      <Row className="team-facts game-data">
                        <Col className="gutter-row" span={12}>
                          <Flex vertical className="dashboard-team-facts">
                            <span className="skill-set-header fw-normal">
                              Addition
                            </span>
                            <Flex>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_add_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="64 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_add_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_add_rules_dd_completed_count ||
                                          0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="100 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_add_rules_dd_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_add_1_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="10k Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_add_1_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                            </Flex>
                          </Flex>
                        </Col>
                        <Col className="gutter-row" span={12}>
                          <Flex vertical className="dashboard-team-facts">
                            <span className="skill-set-header fw-normal">
                              Subtraction
                            </span>
                            <Flex>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_sub_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="64 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_sub_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_sub_rules_dd_completed_count ||
                                          0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="100 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_sub_rules_dd_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_sub_2_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="10k Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_sub_2_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                            </Flex>
                          </Flex>
                        </Col>
                        <Col className="gutter-row" span={12}>
                          <Flex vertical className="dashboard-team-facts">
                            <span className="skill-set-header fw-normal">
                              Multiplication
                            </span>
                            <Flex>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_mul_rules_dd_2_completed_count ||
                                          0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="36 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_mul_rules_dd_2_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_mul_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="100 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_mul_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_mul_1_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="10k Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_mul_1_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                            </Flex>
                          </Flex>
                        </Col>
                        <Col className="gutter-row" span={12}>
                          <Flex vertical className="dashboard-team-facts">
                            <span className="skill-set-header fw-normal">
                              Division
                            </span>
                            <Flex>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_div_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="36 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_div_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_div_rules_dd_completed_count ||
                                          0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="100 Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_div_rules_dd_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip
                                classNames={{ root: 'progress-tooltip' }}
                                placement="top"
                                arrow={false}
                                title={
                                  <div>
                                    <div>
                                      <b>
                                        {teamFluencyGame.vifSummary
                                          .vif_div_1_completed_count || 0}
                                        % Complete
                                      </b>
                                    </div>
                                  </div>
                                }
                              >
                                <div className="player-chart-gym">
                                  <ProgressBar
                                    label="10k Facts"
                                    percent={
                                      teamFluencyGame.vifSummary
                                        .vif_div_1_completed_count || 0
                                    }
                                    showData={
                                      checkedDashboardConfig.showDataOnGrid
                                    }
                                  />
                                </div>
                              </Tooltip>
                            </Flex>
                          </Flex>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {/* Just The Facts Graph */}
                  {checkedDashboardConfig.justTheFacts && (
                    <JustTheFactsGraph
                      teamFluencyGame={teamFluencyGame.jtfSummary}
                      mode={jtfMode}
                      showData={!!checkedDashboardConfig.showDataOnGrid}
                    />
                  )}
                </div>
              ) : (
                <Flex vertical align="center" className="graph-tiles__graph">
                  No data available
                </Flex>
              )}
            </div>
          </Flex>
        </Flex>
      </Col>
      {/* Sidebar html */}
      <Col className="teacher-dashboard__sidebar">
        {checkedDashboardConfig.playersOfTheDay ? (
          <Flex vertical className="section top-players top-players-section">
            <Flex vertical className="player-of-the-day" gap={12}>
              <Flex className="title" justify="space-between" vertical gap={4}>
                <h5 className="title__label player-label">
                  Player{playerOfTheDay.length > 1 && '(s)'} of the Day
                </h5>
                {playerOfTheDay.length === 0 && (
                  <div> Zero stickers earned — no POTD</div>
                )}
              </Flex>
              {playerOfTheDay
                .slice(0, MIN_TOP_PLAYERS)
                .map(({ displayName, userId, stickers }) => {
                  return (
                    <Flex className="potd-list" vertical gap={12} key={userId}>
                      <Flex
                        className="potd-list__item"
                        justify="space-between"
                        align="flex-start"
                        gap={12}
                      >
                        <Flex gap={10} align="flex-start" flex="auto">
                          <Flex
                            className="potd-star potd-icon"
                            justify="center"
                            align="flex-start"
                          >
                            <images.PotdIcon />
                          </Flex>
                          <Flex vertical className="potd-meta" flex="auto">
                            <Flex justify="flex-start" gap={5} vertical>
                              <span className="fw-bold">
                                {displayName !== null && displayName?.length ? (
                                  displayName
                                ) : (
                                  <Link
                                    to={`/team/${team?.id}/team-management/team-roster?noneUserId=${userId}`}
                                  >
                                    None
                                  </Link>
                                )}
                              </span>
                              <span className="fw-bold">
                                ({userId !== null ? userId : 'None'})
                              </span>
                            </Flex>
                            <span className="text-sm text-chalkboard text-stickers">
                              {stickers !== null
                                ? stickers.toLocaleString()
                                : 'None'}{' '}
                              Stickers
                            </span>
                          </Flex>
                        </Flex>
                        <Flex
                          className="potd-trophy trophy-icon"
                          justify="center"
                          align="flex-start"
                        >
                          <images.TrophyIcon />
                          {/* <img
                            src={images.POTDTrophyPng}
                            alt="Player of the day icon"
                          /> */}
                        </Flex>
                      </Flex>
                    </Flex>
                  )
                })}
            </Flex>
            {playerOfTheDay.length > 0 && (
              <Flex className="sticker-count-day fw-bold" align="center">
                <images.StickerCount />+{' '}
                <span className="sticker-count-day__count">
                  {playerOfTheDay[0].stickerDelta}
                </span>
                stickers
                <span className="sticker-count-day__day">Yesterday</span> !
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex vertical className="section top-players top-players-section">
            <Flex vertical className="player-of-the-day" gap={12}>
              <Flex className="title" justify="space-between" vertical gap={4}>
                <h5 className="title__label player-label">
                  Team {playerOfTheDay.length > 1 && '(s)'} of the Month
                </h5>
                {playerOfTheDay.length === 0 && (
                  <div> Zero stickers earned — no POTD</div>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {checkedDashboardConfig.topPlayers && (
          <Flex vertical className="section top-players">
            <Flex className="title" align="center" justify="space-between">
              <h4 className="title__label text-chalkboard">Top Players</h4>
              <div className="title__right">
                <LevelSelect
                  value={day}
                  onChange={(newLevel) => setDay(newLevel)}
                  options={options}
                />
              </div>
            </Flex>
            {topPlayersLoading ? (
              <Spin />
            ) : (
              <Table
                className="top-players-table"
                dataSource={topPlayers.map((player, index) => ({
                  ...player,
                  key: index
                }))} // Ensure unique key for each row
                columns={columns}
                pagination={false}
                scroll={{ y: 296 }}
              />
            )}
          </Flex>
        )}
        {checkedDashboardConfig.teamActivity && (
          <Flex vertical className="section top-players">
            <Flex className="title" align="center" justify="space-between">
              <h4 className="title__label text-chalkboard">Recent Activity</h4>
            </Flex>
            <Flex className="list-wrapper" vertical>
              <ul className="player-activity-list">
                {recentActivityData.map((recentActivity, index) => (
                  <li key={`${recentActivity.displayName ?? 'none'}-${index}`}>
                    <Flex className="player-activity-item">
                      <div className="player-activity-item__indicator">
                        <images.Gameboy />
                      </div>
                      <Flex className="player-activity-item__details" vertical>
                        <div className="">
                          <span className="fw-bold">
                            {recentActivity.displayName &&
                            recentActivity.displayName.length > 0
                              ? recentActivity.displayName
                              : 'None'}
                          </span>{' '}
                          just played{' '}
                          <span className="fw-bold">
                            {recentActivity.gameName}
                          </span>{' '}
                          <div className="text-primary fw-bold">
                            +{recentActivity.stickersAwarded.toLocaleString()}{' '}
                            stickers
                          </div>
                        </div>
                        <span className="text-chalkboard-txt leading-1">
                          {renderPlayed(
                            recentActivity.created.toString() || ''
                          )}
                        </span>
                      </Flex>
                    </Flex>
                  </li>
                ))}
              </ul>
            </Flex>
          </Flex>
        )}
      </Col>
    </Row>
  )
}

export default Dashboard
